import React, { useContext, useEffect, useState } from 'react';
import { Button, Dialog, DialogContent, DialogTitle, Grid, IconButton, TextField } from '@material-ui/core';
import CloudDownloadIcon from '@material-ui/icons/CloudDownload';

import { logisticsApi } from 'api/logistics';
import { outboundTruckApi as api } from 'api/outboundTrucks';
import { UserContext } from 'contexts/UserContext';
import { DataTable, FormComponent, Notifications } from 'shared';
import dayjs from 'utils/dayjsConfig';

function CreateTruckModal({ doors, getDoors, getOutboundTrucks, lineHauls, onClose, open, setNotification }) {
    const formFields = [
        {
            name: 'lineHaulID',
            label: 'Line Haul',
            inputElement: 'autocomplete',
            inputProps: {
                opts: lineHauls.map((item) => ({ value: item.id, text: formatLineHaulForDisplay(item) })),
            },
        },
        {
            name: 'additionalLineHauls',
            label: 'Additional Line Hauls',
            inputElement: 'autocomplete',
            display: (formData) => {
                const lineHaul = lineHauls.find((item) => item.id === formData?.lineHaulID?.value);
                if (lineHaul) {
                    return lineHaul.sort;
                }
                return false;
            },
            dependencies: ['lineHaulID'],
            inputProps: {
                opts: (formData) =>
                    lineHauls
                        .filter((lh) => {
                            const selectedLineHaul = lineHauls.find((item) => item.id === formData.lineHaulID.value);
                            return selectedLineHaul.arrivalDay === lh.arrivalDay && lh.id !== formData.lineHaulID.value;
                        })
                        .map((item) => ({ value: item.id, text: formatLineHaulForDisplay(item) })),
                multiple: true,
            },
        },
        {
            name: 'outboundDoorID',
            label: 'Door',
            inputElement: 'select',
            inputProps: {
                opts: doors
                    .filter((item) => !item.outboundTruckID)
                    .map((item) => ({ text: item.name, value: item.id })),
            },
        },
        { name: 'trailerNumber', label: 'Trailer Number', inputElement: 'textField' },
        {
            name: 'loadType',
            label: 'Load Type',
            inputElement: 'select',
            inputProps: {
                opts: ['DROP_TRAILER', 'LIVE_LOAD'],
            },
        },
    ];
    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="lg">
            <DialogTitle>Create Outbound Truck</DialogTitle>
            <DialogContent>
                <FormComponent
                    formFields={formFields}
                    onSubmit={async (formData) => {
                        const sendParams = {
                            lineHaulID: formData.lineHaulID,
                            outboundDoorID: formData.outboundDoorID,
                            trailerNumber: formData.trailerNumber,
                            additionalLineHaulIDs: formData.additionalLineHauls.map((item) => item.value),
                            loadType: formData.loadType,
                        };
                        const res = await api.createTruck(sendParams);
                        if (!res.status) {
                            setNotification({ text: `Failed to create truck. ${res.msg}`, level: 'error' });
                            return false;
                        }
                        getOutboundTrucks();
                        getDoors();
                        onClose();
                    }}
                />
            </DialogContent>
        </Dialog>
    );
}

let formatLineHaulForDisplay = (lh) => (!lh ? '' : `${lh.carrier} ${lh.hub} ${lh.arrivalDay}`);

function OutboundTrucks() {
    const [notification, setNotification] = useState({ text: null, level: null });
    const [outboundTrucks, setOutboundTrucks] = useState([]);
    const [createTruckModalOpen, setCreateTruckModalOpen] = useState(false);
    const [lineHauls, setLineHauls] = useState([]);
    const [doors, setDoors] = useState([]);
    const [delayReasons, setDelayReasons] = useState([]);
    const user = useContext(UserContext);

    const getDoors = async () => {
        const res = await api.getDoors(user.getFacilityID());
        if (!res.status) {
            setNotification({ text: `Failed to get doors. ${res.msg}`, level: 'error' });
            return;
        }
        setDoors(res.data.doors);
    };

    const getOutboundTrucks = async () => {
        const res = await api.getTrucks(user.getFacilityID());
        if (!res.status) {
            setNotification({ text: `Failed to get outbound trucks. ${res.msg}`, level: 'error' });
            return;
        }
        setOutboundTrucks(
            res.data.rows.map((row) => {
                row.arrivalTime = dayjs(row.arrivalTime.date + row.arrivalTime.timezone)
                    .local()
                    .format('MM/DD/YYYY hh:mm a');
                if (row.departureTime) {
                    row.departureTime = dayjs(row.departureTime.date + row.departureTime.timezone)
                        .local()
                        .format('MM/DD/YYYY hh:mm a');
                }
                return row;
            }),
        );
    };

    const getLineHauls = async () => {
        const res = await logisticsApi.getLineHauls('activeonly');

        if (res.status === false) {
            setNotification({ text: `Failed to get line hauls. ${res.msg}`, level: 'error' });
            return;
        }

        const data = res.data.lineHauls.filter((line) => line.facilityID === user.getFacilityID());
        setLineHauls(data);
    };

    const getDelayedReasons = async () => {
        const res = await api.getTruckDelayReasons();

        if (res.status === false) {
            setNotification({ text: `Failed to get delayed reasons. ${res.msg}`, level: 'error' });
            return;
        }

        setDelayReasons(res.data.rows);
    };

    const columns = [
        { Header: 'ID', accessor: 'id' },
        {
            Header: 'Load Type',
            accessor: 'loadType',
            editable: (row) => row.original.status === 'LOADING',
            editProps: {
                type: 'select',
                options: ['DROP_TRAILER', 'LIVE_LOAD'],
            },
        },
        { Header: 'Arrival Time', accessor: 'arrivalTime', type: 'date' },
        { Header: 'Departure Time', accessor: 'departureTime', type: 'date' },
        {
            Header: 'Status',
            accessor: 'status',
            editable: true,
            editProps: {
                type: 'select',
                options: ['LOADING', 'DEPARTED'],
            },
        },
        {
            Header: 'Trailer Number',
            accessor: 'trailerNumber',
            editable: (row) => row.original.status === 'LOADING',
            editProps: { type: 'input' },
        },
        {
            Header: 'Door',
            accessor: 'outboundDoorID',
            editable: true,
            editProps: {
                type: 'select',
                options: doors.map((item) => ({ text: item.name, value: item.id, disabled: !!item.outboundTruckID })),
            },
        },
        { Header: 'Line Haul ID', accessor: 'lineHaulID' },
        { Header: 'LM Carrier', accessor: 'carrier' },
        { Header: 'Hub', accessor: 'hub' },
        { Header: 'LH Carrier', accessor: 'outboundCarrier' },
        { Header: 'Emails', accessor: 'outboundContacts' },
        { Header: 'Ship Day', accessor: 'arrivalDay' },
        { Header: 'Pallet Count', accessor: 'palletCount' },
        { Header: 'Shipment Count', accessor: 'shipmentCount' },
        {
            Header: 'Additional Line Hauls',
            accessor: 'additionalLineHaulIDs',
            editable: (row) =>
                row.original.status === 'LOADING' &&
                lineHauls
                    .filter((item) => item.sort)
                    .map((item) => item.id)
                    .includes(row.original.lineHaulID),
            editProps: {
                type: 'autocomplete-multiple',
                options: (row) =>
                    lineHauls
                        .filter(
                            (lh) =>
                                lh.id !== row.lineHaulID &&
                                lineHauls.find((lineHaul) => lineHaul.id === row.lineHaulID)?.arrivalDay ===
                                    lh.arrivalDay,
                        )
                        .map((item) => ({ value: item.id, text: formatLineHaulForDisplay(item) })),
                multiple: true,
            },
        },
        { Header: 'Cost', accessor: 'cost' },
        { Header: 'Delayed?', accessor: 'delayed' },
        {
            Header: 'Delayed Reason',
            accessor: 'delayedReason',
            editable: (row) => row.original.delayed === 'YES',
            editProps: {
                type: 'select',
                options: delayReasons,
            },
        },
    ];

    const loadData = async () => {
        getDoors();
        getLineHauls();
        getDelayedReasons();
    };

    useEffect(() => {
        if (lineHauls.length) {
            getOutboundTrucks();
        }
    }, [lineHauls]);

    useEffect(() => {
        loadData();
    }, []);

    return (
        <div style={{ marginTop: 50 }}>
            <Notifications options={notification} />
            <CreateTruckModal
                open={createTruckModalOpen}
                onClose={() => {
                    setCreateTruckModalOpen(false);
                }}
                doors={doors}
                getDoors={getDoors}
                lineHauls={lineHauls}
                setNotification={setNotification}
                getOutboundTrucks={getOutboundTrucks}
            />
            <Grid container style={{ margin: 20 }}>
                {doors.map((door) => (
                    <Grid key={door.id} item xs={12} sm={6} md={2}>
                        <h2>
                            {door.name}
                            <IconButton
                                id={`${door.name.replace(' ', '')}_download`}
                                onClick={() => {
                                    api.getDoorTag(door.id);
                                }}
                            >
                                <CloudDownloadIcon />
                            </IconButton>
                        </h2>
                        {door.outboundTruckID ? (
                            <div style={{ fontSize: 15, fontWeight: 'bold' }}>
                                <div>Truck: {door.outboundTruckID}</div>
                                <div>Carrier: {door.carrier.toUpperCase()}</div>
                                <div>Hub: {door.hub.toUpperCase()}</div>
                                <div>Day: {door.arrivalDay.toUpperCase()}</div>
                                {door.additionalLineHaulIDs?.length > 0 && (
                                    <div>
                                        Additional Line Hauls:{' '}
                                        {door.additionalLineHaulIDs
                                            .map((lhID) =>
                                                formatLineHaulForDisplay(lineHauls.find((item) => item.id === lhID)),
                                            )
                                            .join(', ')}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <div style={{ fontSize: 15, fontWeight: 'bold', color: 'green' }}>OPEN</div>
                        )}
                    </Grid>
                ))}
            </Grid>
            <DataTable
                title="Outbound Trucks"
                columns={columns}
                editable
                data={outboundTrucks}
                csvExport
                toolbarActions={[
                    {
                        name: 'Create Truck',
                        action: () => {
                            setCreateTruckModalOpen(true);
                        },
                    },
                ]}
                saveEdit={async ({ field, id, row, value }) => {
                    const sendParams = { [field]: value };
                    sendParams.lineHaulID = row.lineHaulID;
                    const res = await api.updateTruck(id, sendParams);
                    if (!res.status) {
                        setNotification({ text: `Failed to update truck. ${res.msg}`, level: 'error' });
                        return;
                    }
                    getOutboundTrucks();
                    getDoors();
                }}
            />
        </div>
    );
}

export { OutboundTrucks };
